import { removeItem } from "@/utils/va-order-test/testAndPanelSelection";
import { getStateAbbreviation } from "@/utils/va-order-test/parsePatient.js";
import _ from "lodash";

/**
 * Check if user is a US customer
 * @param {Object} userInfo - User info object
 * @returns {boolean} - True if user is US customer
 */
export function isUSCustomer(userInfo) {
  if (!userInfo) {
    return true; // Default to US if no user info
  }
  const customerCountry = userInfo.customer_country 
    ? userInfo.customer_country.toLowerCase() 
    : '';
  // US country codes/names
  const usCountryValues = ['us', 'usa', 'united states', 'united states of america'];
  return usCountryValues.includes(customerCountry);
}

/**
 * Get the Genetics category test IDs from vwTestCategory
 * @param {Array} vwTestCategory - Array of vw test categories from backend
 * @returns {Array} - Array of test IDs in the Genetics category
 */
export function getGeneticsTestIds(vwTestCategory) {
  if (!vwTestCategory || !Array.isArray(vwTestCategory)) {
    return [];
  }
  const geneticsCategory = vwTestCategory.find(
    category => category.name && category.name.toLowerCase() === 'genetics'
  );
  return geneticsCategory && geneticsCategory.testIds 
    ? geneticsCategory.testIds 
    : [];
}

/**
 * Filter out Genetics category from vwTestCategory for non-US users
 * @param {Array} vwTestCategory - Array of vw test categories from backend
 * @param {Object} userInfo - User info object
 * @returns {Array} - Filtered vwTestCategory
 */
export function filterGeneticsCategoryForNonUS(vwTestCategory, userInfo) {
  if (isUSCustomer(userInfo)) {
    return vwTestCategory;
  }
  return vwTestCategory.filter(
    category => !(category.name && category.name.toLowerCase() === 'genetics')
  );
}

/**
 * Get the NutriProZ ID that should be removed based on user's state
 * Remove NutriProZ(725) for NY customer, & remove NutriProZ NY(726) for non-NY user
 * @param {Object} userInfo - User info object
 * @returns {number|null} - The nutriproz ID to remove, or null if no filtering needed
 */
export function getNutriprozIdToRemove(userInfo) {
  if (!userInfo) {
    return null;
  }
  const rawState = userInfo.customer_state 
    ? userInfo.customer_state 
    : userInfo.state 
      ? userInfo.state 
      : '';
  const userState = rawState ? getStateAbbreviation(rawState) : '';
  const isNYCustomer = userState.toUpperCase() === "NY";
  // Remove NutriProZ(725) for NY customer, & remove NutriProZ NY(726) for non-NY user
  return isNYCustomer ? 725 : 726;
}

export function getCategorizedVwTests(vwCategories, vwBfsList, dbsMap, isDbs, userInfo) {
  let result = isDbs 
    ? getDbsCategorizedTests(vwCategories, vwBfsList, dbsMap)
    : getSerumCategorizedTests(vwCategories, vwBfsList);
  // remove NutriProZ(725) for NY customer, & remove NutriProZ NY(726) for non-NY user
  let nutriprozIdToRemove = getNutriprozIdToRemove(userInfo);
  if (nutriprozIdToRemove) {
    let removedNutriProz = result.reduce((acc, r) => acc.concat(r.testNodeList), [])
      .find(node => node.id === nutriprozIdToRemove);
    // find the category that contain this removed nutriProZ
    // & remove it from this category
    let nutriprozCategory = result.find(t => t.testNodeList.includes(removedNutriProz));
    let nutriprozTestNodes = nutriprozCategory ? nutriprozCategory.testNodeList : [];
    removeItem(removedNutriProz, nutriprozTestNodes);
  }
  let filteredResult = result.filter(vw => vw.testNodeList.length > 0);
  // sort the result list by index in ascending order
  filteredResult.sort((a, b) => a.index - b.index);
  return filteredResult;
  }

function getSerumCategorizedTests(vwCategories, vwBfsList) {
  let result = [];
  vwCategories.forEach(category => {
    let vwNodes = vwBfsList.filter(node => category.contents.includes(node.id)).filter(node => node.value.section !== "at_home");
    // sort vwNodes in category contents' order
    let sortedVwNodes = _.sortBy(vwNodes, function (node) {
      return _.indexOf(category.contents, node.id);
    });
    let nodeSet = new Set(sortedVwNodes);
    // find root for each node
    // sortedVwNodes.forEach(node => {
    //   while (node.parent !== null) {
    //     node = node.parent;
    //   }
    //   nodeSet.add(node);
    // });
    result.push({
      index: category.index,
      categoryId: category.categoryId,
      categoryName: category.categoryName,
      categoryNames: category.categoryNames,
      testNodeList: Array.from(nodeSet),
      isShowAll: false,
    });
  });
  return result;
}

function getDbsCategorizedTests(vwCategories, vwBfsList, dbsMap) {
  let dbsVwTestNodes = vwBfsList.filter(node => ["at_home", "both"].includes(node.value.section));
  let result = [];
  vwCategories.forEach((category) => {
    let nodeSet = new Set();
    category.contents.forEach((c) => {
      // find any dbs eligible nodes for each category
      let dbsNode = dbsVwTestNodes.find(n => n.id === c);
      if (dbsNode) { // if given node is dbs eligible, then add to set and check next
        // while (dbsNode.parent !== null) {
        //   dbsNode = dbsNode.parent;
        // }
        nodeSet.add(dbsNode);
        return;
      }
      let dbsMapping = dbsMap.find((n) => n.originalItemId === c);
      let dbsId = dbsMapping ? dbsMapping.dbsItemId : c;
      dbsNode = dbsVwTestNodes.find(n => n.id === dbsId);
      if (dbsNode) {
        let { blood, dryIce } = dbsNode.value.sampleTypeSets;
        let dbsNotEligible = !dbsMapping && [...blood, ...dryIce].length > 0;
        if (!dbsNotEligible) {
          // while (dbsNode.parent !== null) {
          //   dbsNode = dbsNode.parent;
          // }
          nodeSet.add(dbsNode);
        }
      }
    });
    result.push({
      index: category.index,
      categoryId: category.categoryId,
      categoryName: category.categoryName,
      categoryNames: category.categoryNames,
      testNodeList: Array.from(nodeSet),
      isShowAll: false,
    });
  });
  return result;
}

export function removeVwNodeById(rootNodeList, testNodeList, id) {
  let removedRootNode = rootNodeList.find(node => node.id === id);
  removeItem(removedRootNode, rootNodeList);
  let removedNode = testNodeList.find(node => node.id === id);
  if (removedNode) {
    if (removedNode.parent && removedNode.parent.descendants) {
      removeItem(removedNode, removedNode.parent.descendants);
    }
    removeItem(removedNode, testNodeList);
  }
}